import { MmRequestConfig } from '../../api/mmHttp';
import { MmPaginationState, MmSortingState } from '../../api/pagination';
import { MmBulkReviewAction } from '../../api/results';
import { MmSelectionState } from '../../core/selection';
import { MmMonitoringResult, MmTask } from '../../types/domain';
import { MmMonitoringLevel, MmTaskType } from '../../types/enums';
import {
  MmPage,
  MmResultGroupsResponse,
  MmResultListFilters,
  MmResultsSelectCheckBoxesData,
} from '../../types/dto';

/** Слот с неизвестным именем молча не рендерится, поэтому имена перечислены здесь один раз */
export const MM_TASK_CARD_EXTENSIONS = {
  headerActions: 'taskCardHeaderActions',
  resultsBulkBar: 'taskResultsBulkBar',
  resultsRowActions: 'taskResultsRowActions',
  resultsContext: 'taskResultsContext',
};

export const MM_TASK_CARD_FORWARDED_SLOTS: string[] = [
  MM_TASK_CARD_EXTENSIONS.resultsBulkBar,
  MM_TASK_CARD_EXTENSIONS.resultsRowActions,
];

export interface MmTaskCardExtensionContext {
  /** null — карточка ещё грузится либо это пачка, тогда заполнен batchId */
  taskId: string | null;
  batchId: string | null;
  task: MmTask | null;
  taskType: MmTaskType | null;
  monitoringLevel: MmMonitoringLevel | null;
  tradingNetworkId: number | null;
  reload: () => void;
}

export interface MmTaskCardHeaderActionsPayload extends MmTaskCardExtensionContext {
  taskStatus: string | null;
}

export interface MmTaskResultsBulkBarPayload extends MmTaskCardExtensionContext {
  selection: MmSelectionState<string>;
  /** Учитывает режим «выбрано всё, кроме…» */
  selectedCount: number;
  total: number;
  /** Те же фильтры уходят в массовое действие: бэкенд применяет его по фильтру, а не по ключам */
  filters: Partial<MmResultListFilters>;
  /** Уже нормализована: asc/desc, а не ascend */
  sorting: MmSortingState;
  /** total здесь серверный, по всей выборке */
  pagination: MmPaginationState;
  busy: boolean;
}

export interface MmTaskResultsRowActionsPayload extends MmTaskCardExtensionContext {
  result: MmMonitoringResult;
  /** Индекс строки на текущей странице: годится для ключей v-for, но не для идентичности */
  index: number;
  outcome: string;
}

export type MmResultGroupsLoader = (config?: MmRequestConfig) => Promise<MmResultGroupsResponse>;

export type MmResultsLoader = (
  filters: Partial<MmResultListFilters> & { classificationId?: string | null },
  pageParams: { [key: string]: any },
  config?: MmRequestConfig,
) => Promise<MmPage<MmMonitoringResult>>;

export type MmResultsReviewSubmitter = (
  action: MmBulkReviewAction,
  filters: { [key: string]: any },
  selectCheckBoxesData: MmResultsSelectCheckBoxesData,
  comment: string,
  config?: MmRequestConfig,
) => Promise<{ data: { affected: number } }>;

export interface MmTaskResultsContext {
  scope: 'task' | 'batch';
  taskId: string | null;
  batchId: string | null;
  /** null — настройки таблицы не сохраняются; batchId в ключ не годится, он общий для всех пачек */
  scopeKey: string | null;
  /** null — план только своего задания; в сводном режиме нужен весь список, иначе эталон будет пуст. */
  planTaskIds: string[] | null;
  /** null — задание одно, хватает taskId; список нужен, чтобы строка знала свой магазин */
  scopeTasks: MmTask[] | null;
  /** Полный ключ перевода, а не хвост; null — заголовок по умолчанию */
  titleKey: string | null;
  loadGroups: MmResultGroupsLoader | null;
  /** null — использовать штатный fetchResults() */
  loadResults: MmResultsLoader | null;
  /** null — область по умолчанию { taskIds: [taskId] } */
  scopeFilters: Partial<MmResultListFilters> | null;
  /** false — панель массовых действий и колонка отметок не рисуются вовсе, а не блокируются */
  allowBulkReview: boolean;
  scopeBlockedReasonKey: string | null;
  submitReview: MmResultsReviewSubmitter | null;
  onReviewComplete?: (() => void) | null;
}

export function createDefaultTaskResultsContext(taskId: string | null): MmTaskResultsContext {
  return {
    scope: 'task',
    taskId: taskId,
    batchId: null,
    scopeKey: taskId,
    planTaskIds: null,
    scopeTasks: null,
    titleKey: null,
    loadGroups: null,
    loadResults: null,
    scopeFilters: null,
    allowBulkReview: true,
    scopeBlockedReasonKey: null,
    submitReview: null,
  };
}

/** Смотрим оба вида слотов и зовём методом из шаблона: computed закэширует первый рендер */
export function hasExtension(vm: any, name: string): boolean {
  if (!vm) {
    return false;
  }

  const scoped = vm.$scopedSlots;
  const plain = vm.$slots;

  return Boolean((scoped && scoped[name]) || (plain && plain[name]));
}
